"use client";

import { useAuth } from "@/providers/auth-provider";
import { useModal } from "@/providers/modal-provider";
import { ModalType } from "@/types/modal-type";
import { VentureCapitalListingRecord } from "@/types/venture-capital";
import { MapPinIcon } from "lucide-react";
import { useRouter } from "next/navigation";

export default function VCCard({ vc }: { vc: VentureCapitalListingRecord }) {
	const router = useRouter();
	const { isLoggedIn } = useAuth();
	const { openModal } = useModal();

	const handleVisitVC = () => {
		if (isLoggedIn) {
			router.push(`/vc/${vc.slug}`);
		} else {
			openModal(ModalType.Auth);
		}
	};

	return (
		<button
			type="button"
			onClick={handleVisitVC}
			className="group h-full rounded bg-white p-4 shadow transition-all duration-300 hover:shadow-lg md:w-72"
		>
			<div className="flex h-full w-full flex-col justify-between gap-4">
				<div className="flex items-start justify-start gap-4">
					{vc.logo && (
						// eslint-disable-next-line @next/next/no-img-element
						<img
							src={vc.logo}
							alt={vc.name || "Venture Capital"}
							className="h-12 w-12 rounded object-contain text-brand-primary"
							width={480}
							height={480}
						/>
					)}

					<div className="flex flex-col items-start justify-center gap-1.5">
						<h3 className="text-md font-semibold">{vc.name}</h3>

						<div className="flex items-center gap-2">
							{vc.fundTypeTags?.slice(0, 2)?.map((fundTypeTag, i) => (
								<p
									key={i}
									className="whitespace-nowrap rounded border border-brand-primary px-1 py-0.5 text-xxs text-brand-primary"
								>
									{fundTypeTag}
								</p>
							))}

							{vc.fundTypeTags?.length > 2 && (
								<p className="rounded border border-gray-400 px-1 py-0.5 text-xxs text-gray-600">
									+{vc.fundTypeTags?.length - 2}
								</p>
							)}
						</div>
					</div>
				</div>

				<div className="flex gap-6">
					<div className="flex flex-col items-center gap-2">
						<h4 className="text-xxs font-semibold uppercase text-gray-500">Founded</h4>
						<p className="text-xxs">{vc.foundedYear || "-"}</p>
					</div>
					<div className="flex flex-col items-center gap-2">
						<h4 className="text-xxs font-semibold uppercase text-gray-500">Investment</h4>
						<p className="text-xxs">{vc.totalCompanies || "-"}</p>
					</div>
					<div className="flex flex-col items-center gap-2">
						<h4 className="text-xxs font-bold uppercase text-gray-500">Exits</h4>
						<p className="text-xxs">{vc.totalExits || "-"}</p>
					</div>
				</div>

				<div className="mt-2 flex flex-col items-start gap-2">
					<p className="text-xxs font-semibold uppercase text-gray-500">Investment stage</p>
					<div className="flex items-center gap-2">
						{vc.investmentStagTags?.slice(0, 2)?.map((investmentStagTag, i) => (
							<p
								key={i}
								className="rounded border border-brand-primary px-1 py-0.5 text-xxs text-brand-primary"
							>
								{investmentStagTag}
							</p>
						))}

						{vc.investmentStagTags?.length > 2 && (
							<p className="whitespace-nowrap rounded border border-brand-primary px-1 py-0.5 text-xxs text-brand-primary">
								+{vc.investmentStagTags?.length - 2}
							</p>
						)}
					</div>
				</div>

				<div className="mt-2 flex flex-col items-start gap-2">
					<p className="text-xxs font-semibold uppercase text-gray-500">Sector Focus</p>
					<div className="flex items-center gap-2">
						{vc.sectorFocusTags?.slice(0, 2)?.map((sectorFocusTag, i) => (
							<p
								key={i}
								className="rounded border border-brand-primary px-1 py-0.5 text-xxs text-brand-primary"
							>
								{sectorFocusTag}
							</p>
						))}

						{vc.sectorFocusTags?.length > 2 && (
							<p className="whitespace-nowrap rounded border border-brand-primary px-1 py-0.5 text-xxs text-brand-primary">
								+{vc.sectorFocusTags?.length - 2}
							</p>
						)}
					</div>
				</div>

				{vc.hqLocation && (
					<div className="mt-4 flex items-center gap-1 text-xs">
						<MapPinIcon className="h-3 w-3 " />

						{vc.hqLocation}
					</div>
				)}
			</div>
		</button>
	);
}
